import apiService from '../../../services/api'
import { openExternalPaymentLink } from '../utils/openExternalPaymentLink'

/**
 * CardWW6 (EasyPay / Stripe Checkout) API
 * Оплата международными банковскими картами VISA/MC в долларах через EasyPay (страница Stripe).
 * Структура эндпоинтов идентична cardww1–cardww5, отличается только путь /api/cardww6/
 * и поле `cardww6` в пакетах /api/topup/. Лимиты дробные (минимум $10.15).
 */

/**
 * Получить конфигурацию cardww6 (курсы и лимиты)
 */
export const fetchCardWw6Config = async () => {
  try {
    const response = await apiService.makeRequest('/api/cardww6/')

    if (response.cardww6) {
      return { ...response.cardww6, promo: response.promo || null }
    }

    throw new Error('Invalid response format: cardww6 config not found')
  } catch (error) {
    console.error('Error fetching CardWW6 config:', error)
    throw error
  }
}

/**
 * Получить пакеты для cardww6 из общего списка тарифов /api/topup/
 * Фильтрует только те пакеты, у которых есть поле cardww6 (пакеты вне лимитов метода его не имеют)
 */
export const fetchCardWw6Packages = async () => {
  try {
    const response = await apiService.makeRequest('/api/topup/')

    if (response.topup && Array.isArray(response.topup)) {
      return response.topup.filter((pkg) => pkg.cardww6 !== undefined)
    }

    throw new Error('Invalid response format: topup array not found')
  } catch (error) {
    console.error('Error fetching CardWW6 packages:', error)
    throw error
  }
}

// Нормализация ошибок бэкенда cardww6 к единому виду
const normalizeCardWw6CreateError = (response) => {
  if (!response?.error) return null

  if (response.error === 'error sum') return 'INVALID_AMOUNT'
  if (response.error === 'error backend') return 'BACKEND_ERROR'
  if (response.error === 'error limit') return 'PAYMENT_LIMIT'

  return response.error
}

/**
 * Создать платеж cardww6 по сумме в долларах (округляем до центов)
 */
export const createPaymentByDollars = async (dollars) => {
  try {
    const response = await apiService.makeRequest('/api/cardww6/', {
      method: 'POST',
      body: JSON.stringify({ dollar: Math.round(dollars * 100) / 100 })
    })

    const normalizedError = normalizeCardWw6CreateError(response)
    if (normalizedError) throw new Error(normalizedError)

    if (response.link) return { link: response.link }

    throw new Error('Invalid response format: link not found')
  } catch (error) {
    console.error('Error creating CardWW6 payment by dollars:', error)
    throw error
  }
}

/**
 * Создать платеж cardww6 по количеству кристаллов
 */
export const createPaymentByCrystals = async (crystals) => {
  try {
    const response = await apiService.makeRequest('/api/cardww6/', {
      method: 'POST',
      body: JSON.stringify({ crystal: crystals })
    })

    const normalizedError = normalizeCardWw6CreateError(response)
    if (normalizedError) throw new Error(normalizedError)

    if (response.link) return { link: response.link }

    throw new Error('Invalid response format: link not found')
  } catch (error) {
    console.error('Error creating CardWW6 payment by crystals:', error)
    throw error
  }
}

/**
 * Открыть ссылку на оплату cardww6 как есть (в Telegram — openLink, в браузере — новая вкладка)
 */
export const openCardWw6Payment = (paymentLink, onRedirect, paymentWindow = null) => {
  openExternalPaymentLink(paymentLink, onRedirect, 'CardWW6', paymentWindow)
}

export default {
  fetchCardWw6Config,
  fetchCardWw6Packages,
  createPaymentByDollars,
  createPaymentByCrystals,
  openCardWw6Payment
}
